/**
 * SearchBar Component
 * Search for ads and import them to the Unsorted list
 */

import React, { useState, useEffect } from 'react';
import { useSearchAds, useAddAdsToList } from '../../hooks/useOrganization';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import './SearchBar.css';

interface SearchBarProps {
  tabId: string;
  unsortedListId: string | null;
}

export const SearchBar: React.FC<SearchBarProps> = ({ tabId, unsortedListId }) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showResults, setShowResults] = useState(false);

  const { data: results = [], isLoading } = useSearchAds(debouncedQuery, tabId, debouncedQuery.length > 0);
  const addAds = useAddAdsToList();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleImport = (adId: string) => {
    if (!unsortedListId) return;

    addAds.mutate(
      { listId: unsortedListId, request: { ad_ids: [adId] } },
      {
        onSuccess: () => {
          // Remove from results or close dropdown
          setQuery('');
          setDebouncedQuery('');
          setShowResults(false);
        },
      }
    );
  };

  return (
    <div className="search-bar-container">
      <div className="search-input-wrapper">
        <input
          type="text"
          placeholder="Search ads to import to Unsorted..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          className="search-input"
        />
        {query && (
          <button
            className="clear-search-btn"
            onClick={() => {
              setQuery('');
              setDebouncedQuery('');
              setShowResults(false);
            }}
          >
            ✕
          </button>
        )}
      </div>

      {showResults && query && (
        <div className="search-results-dropdown">
          {isLoading ? (
            <div className="search-result-item loading">Searching...</div>
          ) : results.length === 0 ? (
            <div className="search-result-item empty">
              No ads found
              {debouncedQuery && <div className="empty-query">for "{debouncedQuery}"</div>}
            </div>
          ) : (
            results.map((result) => (
              <div key={result.ad_id} className="search-result-item">
                <div className="search-result-content">
                  <div className="search-result-thumbnail">
                    {result.thumbnail_url ? (
                      <img src={result.thumbnail_url} alt={result.ad_id} />
                    ) : (
                      <div className="thumbnail-placeholder">No Image</div>
                    )}
                  </div>
                  
                  <div className="search-result-info">
                    <div className="search-result-name">{result.ad_id}</div>
                    <div className="search-result-status">
                      <YouTubeIcon
                        sx={{
                          fontSize: 14,
                          color: result.upload_status.youtube === 'uploaded' ? '#FF0000' : '#666666',
                          opacity: result.upload_status.youtube === 'uploaded' ? 1 : 0.3,
                        }}
                        titleAccess={result.upload_status.youtube === 'uploaded' ? 'Uploaded to YouTube' : 'Not uploaded to YouTube'}
                      />
                      <FacebookIcon
                        sx={{
                          fontSize: 14,
                          color: result.upload_status.meta === 'uploaded' ? '#1877F2' : '#666666',
                          opacity: result.upload_status.meta === 'uploaded' ? 1 : 0.3,
                        }}
                        titleAccess={result.upload_status.meta === 'uploaded' ? 'Uploaded to Meta' : 'Not uploaded to Meta'}
                      />
                    </div>
                  </div>
                  
                  <button
                    className="import-btn"
                    onClick={() => handleImport(result.ad_id)}
                    disabled={result.already_in_tab || addAds.isPending}
                  >
                    {result.already_in_tab ? 'In Tab' : 'Import'}
                  </button>
                </div>
                {result.already_in_tab && (
                  <div className="already-in-tab-hint">Already in a list on this tab</div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {showResults && query && (
        <div className="search-backdrop" onClick={() => setShowResults(false)} />
      )}
    </div>
  );
};

